import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TopFrame } from "@/components/dashboard/TopFrame";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import GoalSettingTab from "@/components/performance/GoalSettingTab";
import PerformanceAppraisalTab from "@/components/performance/PerformanceAppraisalTab";
import PerformanceBandingTab from "@/components/performance/PerformanceBandingTab";
import BandDisagreementsTab from "@/components/performance/BandDisagreementsTab";
import AIPerformanceAdvisorTab from "@/components/performance/ai-advisor/AIPerformanceAdvisorTab";
import { usePerformanceStage } from "@/hooks/usePerformanceStage";
import { PerformanceYearProvider, usePerformanceYear } from "@/contexts/PerformanceYearContext";

const queryClient = new QueryClient();

const PerformanceContent = ({ user }: { user: User }) => {
  const { config: performanceConfig, isLoading: configLoading } = usePerformanceStage();
  const { activeYear } = usePerformanceYear();
  const [isUnitHead, setIsUnitHead] = useState(false);
  const [isCheckingUnitHead, setIsCheckingUnitHead] = useState(true);

  // Check if user is a unit head
  useEffect(() => {
    const checkUnitHeadStatus = async () => {
      try {
        const { data: userData, error } = await supabase
          .from('users')
          .select('is_unit_head')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking unit head status:', error);
          setIsUnitHead(false);
        } else {
          setIsUnitHead(userData?.is_unit_head || false);
        }
      } catch (error) {
        console.error('Error in checkUnitHeadStatus:', error);
        setIsUnitHead(false);
      } finally {
        setIsCheckingUnitHead(false);
      }
    };

    checkUnitHeadStatus();
  }, [user.id]);

  if (configLoading || isCheckingUnitHead) {
    return null;
  }

  return (
    <div className="min-h-screen bg-secondary">
      <TopFrame user={user} hideProgress={true} />
      <main className="max-w-7xl mx-auto px-4">
        <div className="mt-8 space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-white">Performance Management</h1>
            <div className="text-sm text-primary bg-primary/10 px-3 py-1 rounded-lg">
              Active Year: {activeYear}
            </div>
          </div>
          
          <Tabs defaultValue="goal-setting" className="w-full">
            <TabsList className="w-full justify-center bg-secondary/50 border border-primary/20">
              <TabsTrigger 
                value="goal-setting" 
                className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black"
                disabled={!performanceConfig.goalsetting_stage}
              >
                Goal Setting
              </TabsTrigger>
              <TabsTrigger 
                value="performance-appraisal" 
                className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black"
                disabled={!performanceConfig.appraisal_stage}
              >
                Performance Appraisal
              </TabsTrigger>
              <TabsTrigger 
                value="performance-banding" 
                className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black"
                disabled={!performanceConfig.banding_stage}
              >
                Performance Banding
              </TabsTrigger>
              <TabsTrigger 
                value="band-disagreements" 
                className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black"
                disabled={!performanceConfig.disagreements_stage}
              >
                Band Disagreements
              </TabsTrigger>
              {isUnitHead && (
                <TabsTrigger 
                  value="ai-performance-advisor" 
                  className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black"
                >
                  AI Performance Advisor (beta)
                </TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="goal-setting">
              {performanceConfig.goalsetting_stage ? (
                <GoalSettingTab />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <p className="text-gray-400 text-lg">Goal Setting is currently disabled</p>
                    <p className="text-gray-500 text-sm mt-2">Please contact your administrator for more information</p>
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="performance-appraisal">
              {performanceConfig.appraisal_stage ? (
                <PerformanceAppraisalTab />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <p className="text-gray-400 text-lg">Performance Appraisal is currently disabled</p>
                    <p className="text-gray-500 text-sm mt-2">Please contact your administrator for more information</p>
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="performance-banding">
              {performanceConfig.banding_stage ? (
                <PerformanceBandingTab />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <p className="text-gray-400 text-lg">Performance Banding is currently disabled</p>
                    <p className="text-gray-500 text-sm mt-2">Please contact your administrator for more information</p>
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="band-disagreements">
              {performanceConfig.disagreements_stage ? (
                <BandDisagreementsTab />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <p className="text-gray-400 text-lg">Band Disagreements is currently disabled</p>
                    <p className="text-gray-500 text-sm mt-2">Please contact your administrator for more information</p>
                  </div>
                </div>
              )}
            </TabsContent>
            
            {isUnitHead && (
              <TabsContent value="ai-performance-advisor">
                <AIPerformanceAdvisorTab />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </main>
    </div>
  );
};

const Performance = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const {
          data: {
            session
          },
          error: sessionError
        } = await supabase.auth.getSession();
        if (sessionError) {
          console.error('Session error:', sessionError);
          throw sessionError;
        }
        if (!session) {
          toast.error("Please sign in to access the performance module");
          navigate('/');
          return;
        }
        setUser(session.user);
      } catch (error) {
        console.error('Authentication error:', error);
        toast.error("Session expired. Please sign in again.");
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
        navigate('/');
        return;
      }
      if (session?.user) {
        setUser(session.user);
      }
    });
    checkSession();
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (isLoading) {
    return null;
  }

  if (!user) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <PerformanceYearProvider>
        <PerformanceContent user={user} />
      </PerformanceYearProvider>
    </QueryClientProvider>
  );
};

export default Performance;
